import { ContractEnums } from '@customer-mono/shared-types';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AutopayInfoWrapper } from 'src/components/global-styles/GlobalStyles';

import { Loader } from '../../components';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { useContracts } from '../../hooks/useContracts';
import { ContractApiResponse } from '../../services/contract-services';
import { RootState } from '../../store';
import { formatCurrency, formatToUSDate, getCurrentPaymentDueInfo } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';

import ActiveList from './activeList';
import ClosedList from './closedList';

interface Contract {
    id: string;
    title: string;
    type: string;
    category: string;
    status: string;
    progress: number;
    lastPayment: string;
    currentPaymentDue: string;
    currentPaymentDueDate: string;
    isCurrentDueActual: boolean;
    isPastDueActual: boolean;
    nextPayment: string;
    amountLabel: string;
    amount: string;
    contractId: string;
    assetDesc?: string; // Asset description from assets API
    apiContract?: ContractApiResponse; // Store original API data
}

// Transform API response to UI format
const transformContract = (
    apiContract: ContractApiResponse,
    t: (key: string) => string,
    assetDesc?: string,
): Contract => {
    // Calculate progress based on payments made
    const bookingDate = new Date(apiContract.BookingDate);
    const maturityDate = new Date(apiContract.MaturityDate);
    const today = new Date();
    const totalDays = Math.ceil((maturityDate.getTime() - bookingDate.getTime()) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((today.getTime() - bookingDate.getTime()) / (1000 * 60 * 60 * 24));
    const progress = Math.min(Math.max(Math.round((daysElapsed / totalDays) * 100), 0), 100);

    // Determine amount label based on contract type
    const amountLabel = apiContract.IsLoan ? t('contracts.list.loanAmount') : t('contracts.list.leaseAmount');
    // Determine contract type based on Status (1 = active, 2 = closed)
    const isActive = apiContract.Status === ContractEnums?.ContractStatusTypes?.active;
    const contractType = isActive ? t('contracts.status.active') : t('contracts.status.closed');
    const currentPaymentDueInfo = getCurrentPaymentDueInfo(apiContract);

    return {
        id: apiContract.Id,
        contractId: apiContract.AspireId,
        title: `Contract ${apiContract.AspireId}`,
        type: contractType,
        category: apiContract.StatusDescr || t('contracts.status.consumer'),
        status: apiContract.StatusDescr || t('contracts.status.progress'),
        progress,
        lastPayment: apiContract.LastPaymentDate ? formatToUSDate(apiContract.LastPaymentDate) : 'N/A',
        currentPaymentDue: formatCurrency(currentPaymentDueInfo.amount),
        currentPaymentDueDate:
            (apiContract.CurrentPaymentDate ?? apiContract.NextPaymentDate)
                ? formatToUSDate((apiContract.CurrentPaymentDate ?? apiContract.NextPaymentDate) as string)
                : 'N/A',
        isCurrentDueActual: currentPaymentDueInfo.isCurrentDueActual,
        isPastDueActual: Number(apiContract.PastDue) > 0,
        nextPayment: apiContract.NextPaymentDate ? formatToUSDate(apiContract.NextPaymentDate) : 'N/A',
        amountLabel,
        amount: formatCurrency(apiContract.PrincipleAmount),
        assetDesc, // Asset description from assets API
        apiContract, // Store original API data for navigation
    };
};
export const ContractsOverview: React.FC = () => {
    const { t } = useTranslation();
    const { contracts: contractsFromStore, fetchContracts } = useContracts();

    const [activeContracts, setActiveContracts] = useState<Contract[]>([]);
    const [closedContracts, setClosedContracts] = useState<Contract[]>([]);
    const [hasFetchedContracts, setHasFetchedContracts] = useState(false);
    const initialContractsFetch = useRef(false);
    const isHttpLoaderActive = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);

    useEffect(() => {
        const fetchContractsWithAssets = () => {
            const contractsToUse = contractsFromStore && contractsFromStore.length > 0 ? contractsFromStore : null;
            if (contractsToUse && contractsToUse.length > 0) {
                const transformedContracts = contractsToUse.map((contract: ContractApiResponse) => {
                    const assetDesc = contract.AssetDesc ?? '';
                    return transformContract(contract, t, assetDesc);
                });

                // Filter by Status number instead of translated strings to avoid dependency on t
                setActiveContracts(
                    transformedContracts.filter(
                        (contract: Contract) =>
                            contract.apiContract?.Status === ContractEnums?.ContractStatusTypes?.active,
                    ),
                );
                setClosedContracts(
                    transformedContracts.filter(
                        (contract: Contract) =>
                            contract.apiContract?.Status === ContractEnums?.ContractStatusTypes?.closed,
                    ),
                );
            }
        };

        fetchContractsWithAssets();
    }, [contractsFromStore, t]);

    useEffect(() => {
        if (initialContractsFetch.current) return;
        initialContractsFetch.current = true;

        fetchContracts(true)
            .catch(err => console.error('[Contracts-Effect] ERROR:', err))
            .finally(() => setHasFetchedContracts(true));
    }, [fetchContracts]);

    const tabItems = useMemo(
        () => [
            {
                label: `${t('contracts.tabs.active')} (${activeContracts?.length})`,
                content: <ActiveList contractList={activeContracts} isFetching={!hasFetchedContracts} />,
            },
            {
                label: `${t('contracts.tabs.closed')} (${closedContracts?.length})`,
                content: <ClosedList contractList={closedContracts} isFetching={!hasFetchedContracts} />,
            },
        ],
        [t, activeContracts, closedContracts, hasFetchedContracts],
    );
    return (
        <Box p={3}>
            {/* Covers the gap before the contracts request starts (global loader not active yet), which
                otherwise renders an empty list now that login no longer prefetches contracts. */}
            {!hasFetchedContracts && !isHttpLoaderActive && <Loader />}
            <CommonHeader
                mainTitle={t('contracts.header.mainTitle')}
                subTitle={t('contracts.header.subTitle')}
                icon="icon-contracts"
            />
            <Box sx={{ mt: 3 }}>
                <AutopayInfoWrapper direction="row" alignItems="center" mb={3}>
                    <span className="icon-info-circle" />
                    <Typography variant="body2">{t('contracts.list.cardTooltip')}</Typography>
                </AutopayInfoWrapper>
                <CommonTabs tabs={tabItems} defaultActiveIndex={0} />
            </Box>
        </Box>
    );
};
